import { PageLoader } from '@/components/ui/page-loader'
import { useAccessControl } from '@/hooks/use-access-control'
import { restoreBrowserSession } from '@/lib/api-client'
import { subscribeToAuthCleared, useAuthStore } from '@/stores/auth.store'
import { useChatStore } from '@/stores/chat-store'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useRef } from 'react'

export function AuthSessionBoundary({
  children,
  requireAuthorization = true,
}: {
  children: ReactNode
  requireAuthorization?: boolean
}) {
  const queryClient = useQueryClient()
  const isInitialized = useAuthStore((state) => state.isInitialized)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const sessionError = useAuthStore((state) => state.sessionError)
  const setSessionError = useAuthStore((state) => state.setSessionError)
  const started = useRef(false)
  const gateAuthorization = useRef(!isInitialized || isAuthenticated)
  const authorization = useAccessControl(requireAuthorization && isInitialized && isAuthenticated)

  // Once session restoration confirms there is no existing account, future
  // authentication is an in-page sign-in and the route-level guards own the
  // authorization handoff without unmounting the router.
  if (isInitialized && !isAuthenticated) gateAuthorization.current = false

  useEffect(() => {
    if (started.current) return
    started.current = true
    void restoreBrowserSession()
  }, [])

  useEffect(
    () =>
      subscribeToAuthCleared(() => {
        queryClient.clear()
        useChatStore.getState().reset()
      }),
    [queryClient],
  )

  if (
    !isInitialized ||
    (gateAuthorization.current &&
      requireAuthorization &&
      isAuthenticated &&
      authorization.isPending)
  ) {
    return <PageLoader />
  }

  if (
    gateAuthorization.current &&
    requireAuthorization &&
    isAuthenticated &&
    authorization.isError
  ) {
    return <AuthorizationUnavailable retry={() => void authorization.refetch()} />
  }

  return (
    <>
      {sessionError && (
        <div
          role="alert"
          className="fixed inset-x-0 top-0 z-[100] bg-destructive px-4 py-3 text-center text-sm text-destructive-foreground shadow-md"
        >
          {sessionError}{' '}
          <button type="button" className="underline" onClick={() => setSessionError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {children}
    </>
  )
}
import { AuthorizationUnavailable } from '@/components/layout/authorization-unavailable'
